import { useState, useEffect } from "react";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import ContentHeader from "../../components/ContentHeader";

const Permission = () => {

	const [isSaving, setIsSaving] = useState(false);
	const [isShopManagerEnabled, setIsShopManagerEnabled] = useState(null);
	const [originalValue, setOriginalValue] = useState(null);
	const [hasChanges, setHasChanges] = useState(false);
	const [isSaved, setIsSaved] = useState(false);

	useEffect(() => {
		loadSettings();
	}, []);

	// Check for changes whenever isShopManagerEnabled changes
	useEffect(() => {
		// Only check for changes if we have loaded the original value
		if (originalValue !== null && isShopManagerEnabled !== null) {
			setHasChanges(originalValue !== isShopManagerEnabled);
		}
	}, [isShopManagerEnabled, originalValue]);

	const handleIsShopManagerEnabled = () => {
		setIsShopManagerEnabled((prev) => !prev);
	};

	const loadSettings = async () => {
		try {
			const response = await apiFetch({
				path: "/plac/v1/permissions",
				method: "GET",
			});
			setIsShopManagerEnabled(response.enable_shop_manager);
			setOriginalValue(response.enable_shop_manager);
			setHasChanges(false);
		} catch (error) {
			console.error("Error loading settings:", error);
			// Set a default value in case of error
			setIsShopManagerEnabled(false);
			setOriginalValue(false);
		}
	};
	const saveSettings = async () => {
		setIsSaving(true);
		setIsSaved(false); // Reset saved state

		try {
			await apiFetch({
				path: "/plac/v1/permissions",
				method: "POST",
				data: {
					_wpnonce: window.placAdminData.nonce,
					enable_shop_manager: isShopManagerEnabled,
				},
			});
			// Update the original value to match the current value
			setOriginalValue(isShopManagerEnabled);
			setHasChanges(false);

			// Show success checkmark after saving
			setTimeout(() => {
				setIsSaving(false);
				setIsSaved(true);

				// Reset to default state after 2s
				setTimeout(() => {
					setIsSaved(false);
				}, 1000);
			}, 500);
		} catch (error) {
			console.error("Error saving settings:", error);
			setIsSaving(false);
		}
	};
	

	const discardChanges = () => {
		setIsShopManagerEnabled(originalValue);
		setHasChanges(false);
	};

	// Only render the actual toggle once we have the data
	const isLoading = isShopManagerEnabled === null;

	return (
		<>
			
			<div className="settings-container">
				<ContentHeader
					title={
						"Give Permission to Those Who Are Necessary to Your Business"
					}
					description={
						"Allow selected user roles for access control so they can easily view as a customer and debug an issue from their perspective."
					}
				/>

				<div className="content-section">
					<div className="leftSection">
						<h2 className="title header-1 primary-text-color semi-bold">
							{__("Permission", "access-control")}
						</h2>
						<p className="description header-2 primary-text-color regular">
							{__(
								"Allow the following user roles to view as customer.",
								"access-control",
							)}
						</p>
					</div>

					<div className="rightSection">
						<div className="setting-item">
							<div className="input-container">
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id="shop-manager-tooltip">
											{__(
												"Enable or disable the Shop Manager role view.",
												"access-control",
											)}
										</Tooltip>
									}
								>
									<img
										src={`${window.placAdminData.pluginUrl}/assets/images/question.svg`}
										alt="icon"
										className="icon"
										style={{ cursor: "pointer" }}
									/>
								</OverlayTrigger>

								<div className="input-field">
									<span className="setting-label header-1 primary-text-color semi-bold">
										{__("Shop Manager", "access-control")}
									</span>
									{isLoading ? (
										<div
											className="pl-access-control-switch-placeholder"
											style={{
												width: "40px",
												height: "20px",
											}}
										></div>
									) : (
										<label className="pl-access-control-switch">
											<input
												type="checkbox"
												checked={isShopManagerEnabled}
												onChange={
													handleIsShopManagerEnabled
												}
											/>
											<span
												className={`pl-access-control-slider round ${
													isShopManagerEnabled
														? "checked"
														: ""
												}`}
											></span>
										</label>
									)}
								</div>
							</div>
							<div className="note header-2 primary-text-color regular">
								{__(
									"Allow the following user roles to view as customer.",
									"access-control",
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="footer-section">
					<button
					className={`save-button purple-bg-40 secondary-text-color ${!hasChanges ? "disabled" : ""}`}
					disabled={isLoading || isSaving || !hasChanges}
					onClick={saveSettings}
				>
					{isSaving ? (
						<div className="saving-changes secondary-text-color">
							<div className="pl-access-control-loading-spinner"></div>
							{__("Changes Saving...", "access-control")}
						</div>
					) : isSaved ? (
						<div className="success-checkmark secondary-text-color">
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/success-tick.svg`}
								alt="quotation-icon"
							/>
							{__("Changes Saved", "access-control")}
						</div>
					) : (
						__("Save Change", "access-control")
					)}
				</button>

					<button
						className={`discard-button purple-text-color btn-border ${!hasChanges ? "disabled" : ""}`}
						disabled={isLoading || !hasChanges}
						onClick={discardChanges}
					>
						{__("Discard Changes", "access-control")}
					</button>
					{hasChanges && (
						<div className="unsaved-changes">
							<img
								src={`${window.placAdminData.pluginUrl}/assets/images/quotation.svg`}
								alt="quotation-icon"
							/>
							{__("Unsaved Changes", "access-control")}
						</div>
					)}
					
				</div>
			</div>
		</>
	);
};

export default Permission;